import { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { useDispatch, useSelector } from 'react-redux';
import { DeeplinkActions } from 'store/actions';
import { AuthSelectors, DeeplinkSelectors } from 'store/selectors';
import { useModal } from 'hooks/index';
import { safeLocalStorage } from 'utils/safeStorage';

const PENDING_KEY = 'neshamahDeeplinkPending';
const COMPLETION_KEY = 'neshamahDeeplinkCompletionPending';
const MAX_AGE = 10 * 60 * 1000;

const AuthModalDynamic = dynamic(() => import('components/auth/AuthModal'), { ssr: false });

type PendingLink = { client: string; external_user_id: string; destination: string; ts: number };

export const writePendingLink = (data: Omit<PendingLink, 'ts'>) => {
  safeLocalStorage.setItem(PENDING_KEY, JSON.stringify({ ...data, ts: Date.now() }));
};

export const readPendingLink = (): PendingLink | null => {
  try {
    const p = JSON.parse(safeLocalStorage.getItem(PENDING_KEY) || 'null');
    return p && Date.now() - p.ts < MAX_AGE ? p : null;
  } catch {
    return null;
  }
};

export const clearPendingLink = () => {
  safeLocalStorage.removeItem(PENDING_KEY);
};

export const clearCompletionKey = () => {
  safeLocalStorage.removeItem(COMPLETION_KEY);
};

const useNeshamahDeeplinkResume = () => {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(AuthSelectors.isAuthenticated);
  const linkAccountState = useSelector(DeeplinkSelectors.linkAccountState);
  const [dispatched, setDispatched] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || dispatched) return;
    const pending = readPendingLink();
    if (!pending) return;
    setDispatched(true);
    dispatch(
      DeeplinkActions.deeplinkLinkAccount.request({
        params: { client: pending.client, external_user_id: pending.external_user_id },
      }),
    );
  }, [isAuthenticated, dispatched, dispatch]);

  useEffect(() => {
    if (!dispatched) return;
    if (linkAccountState.isFulfilled) {
      const pending = readPendingLink();
      clearPendingLink();
      if (pending?.destination) {
        safeLocalStorage.setItem(COMPLETION_KEY, '1');
        window.location.href = pending.destination;
      }
    } else if (linkAccountState.error) {
      const pending = readPendingLink();
      clearPendingLink();
      // Even on linking error (e.g. account_already_linked), redirect the user to
      // the intended destination so they are not left stranded on the home page.
      if (pending?.destination) {
        window.location.href = pending.destination;
      }
    }
  }, [dispatched, linkAccountState.isFulfilled, linkAccountState.error]);
};

export default useNeshamahDeeplinkResume;

export const useDeeplinkCompletionPrompt = () => {
  const [prompted, setPrompted] = useState(false);
  const [showAuthModal] = useModal(AuthModalDynamic);
  const isAuthenticated = useSelector(AuthSelectors.isAuthenticated);
  const currentUser = useSelector(AuthSelectors.currentUser);
  const isHardUser = useSelector(AuthSelectors.isHardUser);

  useEffect(() => {
    if (prompted || !isAuthenticated) return;
    const pending = safeLocalStorage.getItem(COMPLETION_KEY) === '1';
    if (!pending) return;
    // Wait for the full user profile before deciding whether to show the modal.
    // After the hard reload (window.location.href), initSession sets sessionUser
    // from the access token only — is_terms_and_conditions_accepted is undefined
    // until getUserDetail resolves. Without this guard a hard user would
    // incorrectly see the Create Account modal (JPA-8125 regression).
    // Returning early keeps COMPLETION_KEY so the effect re-runs once currentUser loads.
    if (!currentUser) return;
    setPrompted(true);
    safeLocalStorage.removeItem(COMPLETION_KEY);
    if (isHardUser) return;
    // Soft users always see 'join' (Create Account) — mirrors useAuthFlow.ts:119 where
    // !isHardUser always maps to 'join'. The 'interest' variant is only for hard users
    // who have already registered but not yet selected interests (isPreCreateUser), and
    // that path is handled separately in useAuthFlow.
    showAuthModal({ variant: 'join' });
  }, [prompted, isAuthenticated, currentUser, isHardUser, showAuthModal]);
};
